import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { ClockCounterClockwise, DownloadSimple, Eye } from 'phosphor-react';
import {
  InternalFormSignerStep,
  CreditApplicationStep,
  CreditApplicationFieldsValues,
  getBranchSalesRepKey,
  InternalFormP21SyncStatus,
  InternalFormSubmission,
  isBranchSalesRepAssigned,
  toSignerFieldSteps,
} from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  decideInternalFormSubmission,
  getInternalFormSubmission,
  getInternalFormSubmissionPDF,
  retryInternalFormP21Sync,
  revertInternalFormDecision,
  resendInternalFormEmail,
  updateInternalFormSubmissionDetails,
} from '../../../../redux/services/internalFormsService';
import { getSupplierBranches } from '../../../../redux/services/supplierAdditionalDetailsService';
import { AppDropDown } from '../../../general/AppDropDown/AppDropDown';
import CreditAppNotes from '../CreditApplicationComponents/CreditAppNotes';
import InternalFormAnswers from './InternalFormAnswers';
import InternalFormSignatures from './InternalFormSignatures';
import InternalFormHistoryModal from './InternalFormHistoryModal';
import InternalFormCreditReports, {
  InternalFormCreditScoreSummary,
  getPulledCreditReports,
} from './InternalFormCreditReports';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { DillButton, DillModal } from '../../../general/Dill';
import { openDillToast } from '../../../../utils/helpers';
import { InternalFormStatusBadge } from './InternalFormSubmissions';
import ApproveInternalFormModal, {
  ApprovalField,
  EmailTagList,
  InternalFormApproval,
  dedupe,
} from './ApproveInternalFormModal';
import InternalFormDetailsSkeleton from './InternalFormDetailsSkeleton';
import { getFormTypeLabel } from './internalFormUtils';
import { AppPdfPreviewModal } from '../../../general/AppPdfPreviewModal/AppPdfPreviewModal';
import { closeMessageModal, openMessageModal } from '../../../../redux/globalSlices/genericSlice';

// The review GET also returns the template's steps, for laying the answers out like the form.
type InternalFormSubmissionView = Omit<InternalFormSubmission, 'accessToken'> & {
  templateSteps?: CreditApplicationStep[];
  templateSigners?: InternalFormSignerStep[];
  // Who the approve / reject dialogs pre-fill, per the template's decision recipients setting.
  defaultDecisionEmails?: string[];
  // The template never updates P21 on approval.
  skipErpUpdate?: boolean;
  // What approving does to each account, when the template fixes it (else the approver chooses).
  billToAction?: 'UPDATE' | 'NONE' | null;
  shipToAction?: 'UPDATE' | 'CREATE' | 'NONE' | null;
  // Extra details the approve dialog asks for (the template's approvalAdditionalFields).
  approvalAdditionalFields?: ApprovalField[];
};

const formatDate = (date?: Date | string | null) =>
  date ? moment(date).format('MM/DD/YYYY hh:mm A') : '-';

const SYNC_STATUS_STYLES: Record<InternalFormP21SyncStatus, { label: string; className: string }> =
  {
    SUCCESS: { label: 'Updated', className: 'bg-green-50 text-green-700 border-green-200' },
    FAILED: { label: 'Failed', className: 'bg-red-50 text-red-700 border-red-200' },
    SKIPPED: { label: 'Skipped', className: 'bg-gray-100 text-gray-700 border-gray-200' },
  };

const FieldValue = ({ value }: { value: any }) => {
  if (value && typeof value === 'object' && typeof value.url === 'string') {
    return (
      <a
        href={value.url}
        target="_blank"
        rel="noreferrer"
        className="text-sm PRIMARY_500-CLR underline">
        {value.name || 'View file'}
      </a>
    );
  }
  if (typeof value === 'boolean') {
    return <p className="text-sm">{value ? 'Yes' : 'No'}</p>;
  }
  if (value === null || value === undefined || value === '') {
    return <p className="text-sm GREY_400-CLR">-</p>;
  }
  return <p className="text-sm whitespace-pre-wrap break-words">{String(value)}</p>;
};

// Toast wording when an approval or retry leaves ERP changes failed ('' when none did).
const describeSyncFailures = (results: { status: string }[], erpName = 'P21') =>
  results.some((item) => item.status === 'FAILED') ? `some ${erpName} changes failed` : '';

const FieldCell = ({ label, value }: { label: string; value: any }) => (
  <div className="flex flex-col gap-0.5 min-w-0">
    <span className="text-xs font-semibold text-gray-900">{label}</span>
    <FieldValue value={value} />
  </div>
);

// A CONDITIONAL_SELECTED_FIELD's own value is the chosen option; its sub-answers are the
// non-hidden entries in fieldValue, shown beneath it across both columns.
const FieldRow = ({ field }: { field: CreditApplicationFieldsValues }) => {
  const subFields = (field.fieldValue ?? []).filter((item: any) => !item.hidden);
  if (subFields.length === 0) {
    return <FieldCell label={field.name || field.id} value={field.value} />;
  }
  return (
    <div className="md:col-span-2 flex flex-col gap-2">
      <FieldCell label={field.name || field.id} value={field.value} />
      <div className="ml-3 pl-3 border-l border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
        {subFields.map((item) => (
          <FieldCell
            key={item.uniqueId || item.id}
            label={item.name || item.id}
            value={item.value}
          />
        ))}
      </div>
    </div>
  );
};

// Same card as the credit application details page's sections.
const SectionCard = ({
  id,
  title,
  action,
  children,
}: {
  id: string;
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div id={id} className="shrink-0 rounded-xl border border-gray-200 overflow-hidden scroll-mt-2">
    <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
      <p className="text-base font-bold text-gray-900">{title}</p>
      {action}
    </div>
    <div className="px-4 py-3">{children}</div>
  </div>
);

const sectionAnchor = (title: string) =>
  `internal-form-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

const toText = (value: unknown) => (value === null || value === undefined ? '' : `${value}`.trim());

// The dropdowns save whichever of id / ERP id / name their value source is set to.
const matchesAny = (selected: string, candidates: unknown[]) =>
  !!selected &&
  candidates.map((candidate) => toText(candidate).toLowerCase()).includes(selected.toLowerCase());

const formatBranchLabel = (branch?: { branchName?: string; erpId?: string }) =>
  branch?.branchName ? `${branch.branchName}${branch.erpId ? ` (${branch.erpId})` : ''}` : '';

// The Branch / Sales Rep answer: a signer's own field first, then the form's, then one nested in a
// conditional answer (e.g. the Maintenance Form's sales rep under a new shipping address).
const findAnswer = (submission: InternalFormSubmissionView | null | undefined, idPart: string) => {
  const answers = [...(submission?.signerFieldValues ?? []), ...(submission?.fieldValues ?? [])];
  const topLevel = answers.find((field) => field.id.includes(idPart));
  if (topLevel) return toText(topLevel.value);
  return toText(
    answers
      .flatMap((field) => field.fieldValue ?? [])
      .find((nested) => nested.id.includes(idPart) && toText(nested.value))?.value
  );
};

const InternalFormSubmissionDetails = () => {
  const dispatch = useAppDispatch();
  const { internalFormSubmissionId } = useParams();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  // Eclipse suppliers' approvals update Eclipse (eclipseSyncResults) instead of P21.
  const isEclipse = currentSupplier?.integrationType === 'ECLIPSE';
  const [submission, setSubmission] = useState<InternalFormSubmissionView | null>(null);
  // Only the review page's GET loads the pulled credit reports; other responses (notes, branch,
  // P21 retry) keep the ones already on screen.
  const setSubmissionKeepingReports = (updated: InternalFormSubmissionView) =>
    setSubmission((prev) => ({
      ...updated,
      nacmReports: updated.nacmReports ?? prev?.nacmReports,
      creditSafeReports: updated.creditSafeReports ?? prev?.creditSafeReports,
      experianReports: updated.experianReports ?? prev?.experianReports,
      manualCreditReports: updated.manualCreditReports ?? prev?.manualCreditReports,
      templateSteps: updated.templateSteps ?? prev?.templateSteps,
      templateSigners: updated.templateSigners ?? prev?.templateSigners,
      defaultDecisionEmails: updated.defaultDecisionEmails ?? prev?.defaultDecisionEmails,
      skipErpUpdate: updated.skipErpUpdate ?? prev?.skipErpUpdate,
      billToAction: updated.billToAction ?? prev?.billToAction,
      shipToAction: updated.shipToAction ?? prev?.shipToAction,
      approvalAdditionalFields: updated.approvalAdditionalFields ?? prev?.approvalAdditionalFields,
    }));

  const [isLoading, setIsLoading] = useState(true);
  // The rejection reason, entered in the Reject dialog.
  const [notes, setNotes] = useState('');
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState<'preview' | 'download' | null>(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState('');
  const [rejectNotifyEmails, setRejectNotifyEmails] = useState<string[]>([]);
  const [decidingAs, setDecidingAs] = useState<'APPROVED' | 'REJECTED' | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRevertModalOpen, setIsRevertModalOpen] = useState(false);
  const [revertReason, setRevertReason] = useState('');
  const [isReverting, setIsReverting] = useState(false);
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const { branches: supplierBranches, loadedSupplierId: loadedSupplierBranchesId } = useAppSelector(
    (state) => state.supplierBranches
  );
  const [isEditingBranch, setIsEditingBranch] = useState(false);
  const [draftBranchId, setDraftBranchId] = useState('');
  const [draftSalesRepId, setDraftSalesRepId] = useState('');
  const [isSavingBranch, setIsSavingBranch] = useState(false);
  const isSalesRole = !!user?.roles?.includes('CREDIT_APP_SALES');

  useEffect(() => {
    if (currentSupplier?.id && loadedSupplierBranchesId !== currentSupplier.id) {
      dispatch(getSupplierBranches({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, loadedSupplierBranchesId, dispatch]);

  // What the credit team assigned, else the Branch / Sales Rep answers (a signer's own field first,
  // then the form's), else (for the branch) the sender's branch.
  const currentBranch = useMemo(() => {
    if (submission?.assignedBranchId) {
      return supplierBranches.find((branch) => branch.branchId === submission.assignedBranchId);
    }
    const answer = findAnswer(submission, 'supplier-branches');
    return (
      supplierBranches.find((branch) =>
        matchesAny(answer, [branch.branchId, branch.erpId, branch.branchName])
      ) ?? supplierBranches.find((branch) => branch.branchId === submission?.branchId)
    );
  }, [submission, supplierBranches]);
  const currentSalesReps = useMemo(() => {
    const allSalesReps = supplierBranches.flatMap((branch) => branch.salesRep ?? []);
    if ((submission?.assignedSalesRepIds ?? []).length > 0) {
      return allSalesReps.filter((rep) =>
        isBranchSalesRepAssigned(rep, submission?.assignedSalesRepIds)
      );
    }
    const answer = findAnswer(submission, 'supplier-sales-reps');
    const rep = allSalesReps.find((candidate) =>
      matchesAny(answer, [candidate?.erpId, candidate?.id, candidate?.name])
    );
    return rep ? [rep] : [];
  }, [submission, supplierBranches]);

  const saveDetails = async (details: {
    internalNotes?: InternalFormSubmission['internalNotes'];
    assignedBranchId?: string | null;
    assignedSalesRepIds?: string[];
  }) => {
    if (!currentSupplier?.id || !internalFormSubmissionId) return false;
    const result = await dispatch(
      updateInternalFormSubmissionDetails({
        supplierId: currentSupplier.id,
        internalFormSubmissionId,
        ...details,
      })
    );
    if (!result.type.includes('fulfilled')) return false;
    const updated: InternalFormSubmissionView | null = (result as any).payload?.data ?? null;
    if (updated) setSubmissionKeepingReports(updated);
    return true;
  };

  const handleSaveBranch = async () => {
    setIsSavingBranch(true);
    const isSaved = await saveDetails({
      assignedBranchId: draftBranchId || null,
      assignedSalesRepIds: draftSalesRepId ? [draftSalesRepId] : [],
    });
    setIsSavingBranch(false);
    if (isSaved) {
      setIsEditingBranch(false);
      openDillToast({ type: 'SUCCESS', message: 'Branch & sales rep updated' });
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to update branch & sales rep' });
    }
  };

  const draftBranch = supplierBranches.find((branch) => branch.branchId === draftBranchId);

  const handleResendEmail = async () => {
    if (!currentSupplier?.id || !internalFormSubmissionId) return;
    setIsResendingEmail(true);
    const result = await dispatch(
      resendInternalFormEmail({ supplierId: currentSupplier.id, internalFormSubmissionId })
    );
    setIsResendingEmail(false);
    if (result.type.includes('fulfilled')) {
      const updated: InternalFormSubmissionView | null = (result as any).payload?.data ?? null;
      if (updated) setSubmissionKeepingReports(updated);
      openDillToast({ type: 'SUCCESS', message: 'Email resent' });
    } else {
      openDillToast({
        type: 'ERROR',
        message: (result as any).payload || 'Failed to resend the email',
      });
    }
  };

  const handleRevertDecision = async () => {
    if (!currentSupplier?.id || !internalFormSubmissionId) return;
    setIsReverting(true);
    const result = await dispatch(
      revertInternalFormDecision({
        supplierId: currentSupplier.id,
        internalFormSubmissionId,
        reason: revertReason.trim() || undefined,
      })
    );
    setIsReverting(false);
    if (result.type.includes('fulfilled')) {
      const updated: InternalFormSubmissionView | null = (result as any).payload?.data ?? null;
      if (updated) setSubmissionKeepingReports(updated);
      setIsRevertModalOpen(false);
      setRevertReason('');
      openDillToast({
        type: 'SUCCESS',
        message: 'Decision reverted — the form is ready for review',
      });
    } else {
      openDillToast({
        type: 'ERROR',
        message: (result as any).payload || 'Failed to revert the decision',
      });
    }
  };

  const handleRetryP21Sync = async () => {
    if (!currentSupplier?.id || !internalFormSubmissionId) return;
    setIsRetrying(true);
    const result = await dispatch(
      retryInternalFormP21Sync({ supplierId: currentSupplier.id, internalFormSubmissionId })
    );
    setIsRetrying(false);
    if (result.type.includes('fulfilled')) {
      const updated: InternalFormSubmissionView | null = (result as any).payload?.data ?? null;
      if (updated) setSubmissionKeepingReports(updated);
      const failureMessage = describeSyncFailures(updated?.p21SyncResults ?? []);
      openDillToast({
        type: failureMessage ? 'ERROR' : 'SUCCESS',
        message: failureMessage ? `Still failing: ${failureMessage}` : 'P21 updated',
      });
    } else {
      openDillToast({
        type: 'ERROR',
        message: (result as any).payload || 'Failed to retry the P21 update',
      });
    }
  };

  const fetchSubmission = useCallback(async () => {
    if (!currentSupplier?.id || !internalFormSubmissionId) return;
    setIsLoading(true);
    const result = await dispatch(
      getInternalFormSubmission({ supplierId: currentSupplier.id, internalFormSubmissionId })
    );
    setIsLoading(false);
    if (result.type.includes('fulfilled')) {
      setSubmission((result as any).payload?.data ?? null);
    } else {
      openDillToast({
        type: 'ERROR',
        message: (result as any).payload || 'Failed to load internal form',
      });
    }
  }, [currentSupplier?.id, internalFormSubmissionId, dispatch]);

  useEffect(() => {
    fetchSubmission();
  }, [fetchSubmission]);

  // Grouped the way the form was laid out: step, then section.
  const groupedFields = useMemo(() => {
    const groups: {
      key: string;
      stepName: string;
      groupName: string;
      title: string;
      fields: CreditApplicationFieldsValues[];
    }[] = [];
    for (const field of submission?.fieldValues ?? []) {
      const key = `${field.stepName}::${field.groupName}`;
      const existing = groups.find((group) => group.key === key);
      if (existing) {
        existing.fields.push(field);
      } else {
        groups.push({
          key,
          stepName: field.stepName,
          groupName: field.groupName,
          title: field.groupName || field.stepName || 'Form',
          fields: [field],
        });
      }
    }
    return groups;
  }, [submission?.fieldValues]);

  const [activeStep, setActiveStep] = useState('Summary');
  // Template steps that have at least one answer (an unfilled form shows none), then signer steps'
  // own fields (e.g. Internal Information) that were answered.
  const answerSteps = useMemo(() => {
    const formSteps = (submission?.fieldValues?.length ? submission.templateSteps ?? [] : [])
      .slice()
      .sort((a, b) => a.position - b.position);
    const answeredSignerSteps = toSignerFieldSteps(submission?.templateSigners ?? []).filter(
      (step) =>
        (submission?.signerFieldValues ?? []).some((field) => field.signerStepId === step.id)
    );
    return [...formSteps, ...answeredSignerSteps];
  }, [
    submission?.templateSteps,
    submission?.fieldValues,
    submission?.templateSigners,
    submission?.signerFieldValues,
  ]);
  const answerFieldValues = useMemo(
    () => [...(submission?.fieldValues ?? []), ...(submission?.signerFieldValues ?? [])],
    [submission?.fieldValues, submission?.signerFieldValues]
  );
  const [isCreditReportPickerOpen, setIsCreditReportPickerOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const pulledCreditReports = useMemo(() => getPulledCreditReports(submission), [submission]);
  const navSteps = [
    'Summary',
    ...(answerSteps.length > 0
      ? answerSteps.map((step) => step.name)
      : groupedFields.map((group) => group.title)),
    'Signatures',
    'Credit Score & Reports',
    ...(submission?.status === 'APPROVED' ? [isEclipse ? 'Eclipse Updates' : 'P21 Updates'] : []),
  ];

  // Keeps the Steps list in sync while scrolling: the last section whose top has reached the top
  // of the scrolling area (or the last one, at the very bottom).
  const handleDocumentScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const container = event.currentTarget;
    const containerTop = container.getBoundingClientRect().top;
    let current = navSteps[0];
    navSteps.forEach((step) => {
      const element = document.getElementById(sectionAnchor(step));
      if (element && element.getBoundingClientRect().top - containerTop <= 40) current = step;
    });
    if (container.scrollTop + container.clientHeight >= container.scrollHeight - 4) {
      current = navSteps[navSteps.length - 1];
    }
    if (current && current !== activeStep) setActiveStep(current);
  };

  const handlePdf = async (type: 'preview' | 'download') => {
    if (!currentSupplier?.id || !internalFormSubmissionId) return;
    setPdfLoading(type);
    const result = await dispatch(
      getInternalFormSubmissionPDF({
        supplierId: currentSupplier.id,
        internalFormSubmissionId,
        preview: type === 'preview',
      })
    );
    setPdfLoading(null);
    const pdfUrl = (result as any).payload?.data?.pdfUrl;
    if (!result.type.includes('fulfilled') || !pdfUrl) {
      openDillToast({ type: 'ERROR', message: 'Failed to fetch PDF' });
      return;
    }
    if (type === 'preview') setPreviewPdfUrl(pdfUrl);
  };

  const canDecide =
    submission?.status === 'SUBMITTED' && !user?.roles?.includes('CREDIT_APP_SALES');

  const handleDecision = async (
    decision: 'APPROVED' | 'REJECTED',
    approval?: InternalFormApproval
  ) => {
    if (!currentSupplier?.id || !internalFormSubmissionId) return;
    if (decision === 'REJECTED' && !notes.trim()) {
      openDillToast({ type: 'ERROR', message: 'Add a note explaining the rejection' });
      return;
    }
    setDecidingAs(decision);
    // Approving can take a while — it waits on P21 — so it gets the same full-screen loader as a
    // credit application approval.
    if (decision === 'APPROVED') {
      const isUpdatingErp = !!approval && approval.customerAction !== 'none';
      dispatch(
        openMessageModal({
          buttonText: '',
          modalType: 'LOADING',
          title: 'Processing internal form approval',
          subTitle: `This may take a few minutes. ${
            isUpdatingErp ? 'Customer in your ERP is being updated.' : ''
          } Please do not refresh the page.`,
        })
      );
    }
    const result = await dispatch(
      decideInternalFormSubmission({
        supplierId: currentSupplier.id,
        internalFormSubmissionId,
        decision,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        ...(decision === 'REJECTED' ? { notifyEmails: rejectNotifyEmails } : {}),
        ...(approval ?? {}),
      })
    );
    if (decision === 'APPROVED') {
      dispatch(closeMessageModal());
    }
    setDecidingAs(null);
    if (result.type.includes('fulfilled')) {
      setIsApproveModalOpen(false);
      const updated: InternalFormSubmissionView | null = (result as any).payload?.data ?? null;
      const failureMessage = isEclipse
        ? describeSyncFailures(updated?.eclipseSyncResults ?? [], 'Eclipse')
        : describeSyncFailures(updated?.p21SyncResults ?? []);
      openDillToast({
        type: decision === 'APPROVED' && failureMessage ? 'ERROR' : 'SUCCESS',
        message:
          decision === 'REJECTED'
            ? 'Form rejected'
            : failureMessage
            ? `Form approved, but ${failureMessage}`
            : 'Form approved',
      });
      setNotes('');
      setIsRejectModalOpen(false);
      fetchSubmission();
    } else {
      openDillToast({
        type: 'ERROR',
        message: (result as any).payload || 'Failed to update form',
      });
    }
  };

  if (isLoading && !submission) {
    return <InternalFormDetailsSkeleton />;
  }
  if (!submission) {
    return <div className="p-4 text-sm GREY_500-CLR">This internal form could not be found.</div>;
  }

  // Top-level answers, then the non-hidden answers nested in conditional fields.
  const formValue = (id: string) => {
    const topLevel = submission.fieldValues.find((field) => field.id === id);
    const nested = submission.fieldValues
      .flatMap((field) => field.fieldValue ?? [])
      .find((item: any) => item.id === id && !item.hidden);
    return `${(topLevel ?? nested)?.value ?? ''}`.trim();
  };
  // Maintenance Form: an existing shipping address being changed ("Is this a new address?" = No)
  // names its Ship-To in shipping-account-number.
  // The credit limit the approval pushes (Credit Limit Increase / Maintenance forms; a Job
  // Information Sheet's Estimated Job $ Amount, which its Sales Rep fills in).
  const approvedCreditLimitRaw = (
    formValue('new-credit-limit') ||
    formValue('amount-of-credit-requested') ||
    `${
      submission.signerFieldValues?.find((field) => field.id === 'estimated-job-amount')?.value ??
      ''
    }`
  ).replace(/[$,\s]/g, '');
  const approvedCreditLimit =
    approvedCreditLimitRaw && !isNaN(Number(approvedCreditLimitRaw))
      ? Number(approvedCreditLimitRaw).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        })
      : '';
  // The approved card's rows; one with nothing to show is left out.
  const approvalSummaryRows = [
    { label: 'Customer Account', value: submission.billToCustomerId },
    { label: 'Ship-To Account', value: submission.shipToAccountNumber },
    {
      label: 'Amount Approved',
      value:
        typeof submission.approvedCreditLimit === 'number'
          ? submission.approvedCreditLimit.toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
            })
          : approvedCreditLimit,
    },
    { label: 'Terms', value: submission.p21CreateAsCod ? 'COD' : '' },
    ...(submission.approvalAdditionalFieldValues ?? []).map((field) => {
      const isCurrency =
        submission.approvalAdditionalFields?.find((item) => item.id === field.id)?.type ===
        'currency';
      return {
        label: field.name,
        value:
          isCurrency && field.value && !isNaN(Number(field.value))
            ? Number(field.value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
            : field.value,
      };
    }),
  ].filter((row) => !!row.value);
  const isChangingExistingShipping =
    formValue('shipping-address-change').toLowerCase() === 'yes' &&
    formValue('is-new-shipping-address').toLowerCase() === 'no';
  // Current Maintenance Form: "Does this include shipping addresses changes?" — the form doesn't say
  // which Ship-To, so the approver has to give its number.
  const hasShippingChanges = formValue('is-shipping-address-changes').toLowerCase() === 'yes';
  // The sender and everyone who signed — notified of the decision either way.
  const defaultNotifyEmails =
    submission.defaultDecisionEmails ??
    [
      submission.sentByEmail,
      submission.signature?.signedByEmail ?? '',
      ...(submission.signatures ?? []).map((signature) => signature.signedByEmail),
    ].filter(Boolean);

  return (
    // On desktop the document and the side column scroll independently, like the credit
    // application details page; stacked on smaller screens, the page scrolls as one.
    <div
      className="h-full lg:h-[calc(100vh-54px)] p-4 overflow-y-auto lg:overflow-hidden"
      onScroll={handleDocumentScroll}>
      <DillModal
        open={isRejectModalOpen}
        onClose={() => decidingAs !== 'REJECTED' && setIsRejectModalOpen(false)}
        title={`Reject ${getFormTypeLabel(submission)}`}
        width="480px"
        maxWidth="480px"
        footer={
          <div className="flex justify-end w-full gap-2">
            <DillButton
              label="Cancel"
              variant="secondary"
              onClick={() => setIsRejectModalOpen(false)}
              isDisabled={decidingAs === 'REJECTED'}
            />
            <DillButton
              label="Reject"
              onClick={() => handleDecision('REJECTED')}
              isLoading={decidingAs === 'REJECTED'}
              isDisabled={!!decidingAs || !notes.trim()}
            />
          </div>
        }>
        <div className="flex flex-col gap-4">
          <AppInputField
            label="Reason for rejecting"
            isRequired
            inputType="textarea"
            value={notes}
            onTextChange={(text: string) => setNotes(text)}
          />
          <EmailTagList
            label="Emails to notify on rejection"
            emails={rejectNotifyEmails}
            onChange={setRejectNotifyEmails}
          />
        </div>
      </DillModal>
      <DillModal
        open={isRevertModalOpen}
        onClose={() => !isReverting && setIsRevertModalOpen(false)}
        title="Revert Decision"
        width="480px"
        maxWidth="480px"
        footer={
          <div className="flex justify-end w-full gap-2">
            <DillButton
              label="Cancel"
              variant="secondary"
              onClick={() => setIsRevertModalOpen(false)}
              isDisabled={isReverting}
            />
            <DillButton
              label="Revert"
              onClick={handleRevertDecision}
              isLoading={isReverting}
              isDisabled={isReverting}
            />
          </div>
        }>
        <div className="flex flex-col gap-3">
          <p className="text-sm GREY_600-CLR">
            {`This puts the form back to Ready for Decision so it can be approved or rejected again.${
              submission.status === 'APPROVED' && submission.p21SyncResults.length > 0
                ? ' Changes already pushed to P21 are not undone.'
                : ''
            }`}
          </p>
          <AppInputField
            label="Reason (optional)"
            inputType="textarea"
            value={revertReason}
            onTextChange={(text: string) => setRevertReason(text)}
          />
        </div>
      </DillModal>
      <AppPdfPreviewModal
        open={!!previewPdfUrl}
        handleClose={() => setPreviewPdfUrl('')}
        invoice={''}
        pdfType={'CREDIT_APPLICATION'}
        pdfList={[{ url: previewPdfUrl, date: new Date(), name: '' }]}
        handleDownloadPdf={() => {}}
        mode="Supplier"
      />
      <InternalFormHistoryModal
        open={isHistoryModalOpen}
        handleClose={() => setIsHistoryModalOpen(false)}
        history={submission.history}
        formName={submission.templateName || getFormTypeLabel(submission)}
      />
      <ApproveInternalFormModal
        open={isApproveModalOpen}
        handleClose={() => setIsApproveModalOpen(false)}
        onConfirm={(approval) => handleDecision('APPROVED', approval)}
        isErpUpdateDisabled={!!submission.skipErpUpdate}
        approvalFields={submission.approvalAdditionalFields ?? []}
        // The template's action when it sets one; else no customer number on the form means a new
        // account: create it.
        defaultCustomerAction={
          submission.billToAction || submission.shipToAction
            ? submission.shipToAction === 'CREATE'
              ? 'create'
              : 'update'
            : submission.billToCustomerId || formValue('customer-account-number')
            ? 'update'
            : 'create'
        }
        billToAction={submission.billToAction ?? null}
        shipToAction={submission.shipToAction ?? null}
        requestedCreditLimit={
          approvedCreditLimitRaw && !isNaN(Number(approvedCreditLimitRaw))
            ? Number(approvedCreditLimitRaw)
            : undefined
        }
        // Cash-account forms (e.g. the CASH Account Coversheet) create COD customers.
        defaultCreateAsCod={/cash/i.test(submission.templateName ?? '')}
        defaultNotifyEmails={defaultNotifyEmails}
        isApproving={decidingAs === 'APPROVED'}
        defaultBillToCustomerId={
          submission.billToCustomerId || formValue('customer-account-number')
        }
        defaultShipToAccountNumber={
          submission.shipToAccountNumber ||
          formValue('ship-to-account-number') ||
          formValue('shipping-account-number')
        }
        // The "Change to Ship-To Address" section submits new-ship-to-* answers.
        isShipToRequired={
          submission.fieldValues.some((field) => field.id.startsWith('new-ship-to-')) ||
          isChangingExistingShipping ||
          hasShippingChanges
        }
      />
      <div className="flex flex-col lg:flex-row gap-4 lg:h-full">
        <div className="w-full lg:w-3/4 min-w-0 lg:h-full">
          {/* Like the credit application: the card fills the column, its header stays put, and
              only the sections under it scroll. */}
          <div className="bg-white p-4 rounded-2xl border border-gray-200 flex flex-col gap-4 lg:h-full lg:overflow-hidden">
            <div className="flex items-center justify-between gap-2 pb-2 border-b border-gray-200 shrink-0">
              <div className="flex flex-col">
                <p className="text-lg font-bold GREY_900-CLR">{submission.templateName}</p>
                <p className="text-xs GREY_500-CLR">{getFormTypeLabel(submission)}</p>
              </div>
              <div className="flex items-center gap-2">
                <InternalFormStatusBadge status={submission.status} />
                {/* Once filled in — while it's still with later signers too. */}
                {submission.status !== 'SENT' && (
                  <>
                    <DillButton
                      label="Preview"
                      variant="secondary"
                      height={35}
                      className="flex justify-center rounded-md"
                      icon={<Eye size={16} weight="bold" />}
                      onClick={() => handlePdf('preview')}
                      isLoading={pdfLoading === 'preview'}
                      isDisabled={!!pdfLoading}
                    />
                    <DillButton
                      label="Download"
                      variant="secondary"
                      height={35}
                      className="flex justify-center rounded-md"
                      icon={<DownloadSimple size={16} weight="bold" />}
                      onClick={() => handlePdf('download')}
                      isLoading={pdfLoading === 'download'}
                      isDisabled={!!pdfLoading}
                    />
                  </>
                )}
                <DillButton
                  label="View History"
                  variant="secondary"
                  height={35}
                  className="flex justify-center rounded-md"
                  icon={<ClockCounterClockwise size={16} weight="bold" />}
                  onClick={() => setIsHistoryModalOpen(true)}
                />
              </div>
            </div>
            <div
              className="flex flex-col gap-4 lg:flex-1 lg:min-h-0 lg:overflow-y-auto lg:pr-1"
              onScroll={handleDocumentScroll}>
              <SectionCard id={sectionAnchor('Summary')} title="Summary">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                  <FieldCell label="Customer #" value={submission.billToCustomerId} />
                  <FieldCell
                    label="Sent By"
                    value={submission.sentByName || submission.sentByEmail}
                  />
                  <FieldCell label="Sent" value={formatDate(submission.createdAt)} />
                  <FieldCell label="Submitted" value={formatDate(submission.submittedAt)} />
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <InternalFormCreditScoreSummary
                    pulledCreditReports={pulledCreditReports}
                    onViewFullReport={() => {
                      setActiveStep('Credit Score & Reports');
                      document
                        .getElementById(sectionAnchor('Credit Score & Reports'))
                        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  />
                </div>
                {submission.status === 'SENT' && (
                  <p className="text-sm GREY_500-CLR mt-3">
                    {`Waiting for ${submission.recipientEmails.join(
                      ', '
                    )} to fill in and sign this form.`}
                  </p>
                )}
                {submission.status === 'AWAITING_SIGNATURES' && (
                  <p className="text-sm GREY_500-CLR mt-3">
                    {`Waiting for ${(submission.currentSignerEmails ?? []).join(
                      ', '
                    )} to review and sign this form.`}
                  </p>
                )}
              </SectionCard>

              {answerSteps.length > 0
                ? answerSteps.map((step) => (
                    <SectionCard key={step.id} id={sectionAnchor(step.name)} title={step.name}>
                      <InternalFormAnswers steps={[step]} fieldValues={answerFieldValues} />
                    </SectionCard>
                  ))
                : groupedFields.map((group) => (
                    <SectionCard
                      key={group.key}
                      id={sectionAnchor(group.title)}
                      title={group.title}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                        {group.fields.map((field) => (
                          <FieldRow key={field.uniqueId} field={field} />
                        ))}
                      </div>
                    </SectionCard>
                  ))}

              <SectionCard id={sectionAnchor('Signatures')} title="Signatures">
                <InternalFormSignatures
                  submission={submission}
                  signers={submission.templateSigners ?? []}
                  // Only before the form is filled in, same as the list's row menu.
                  onResendEmail={submission.status === 'SENT' ? handleResendEmail : undefined}
                  isResending={isResendingEmail}
                />
              </SectionCard>

              <div id={sectionAnchor('Credit Score & Reports')} className="shrink-0 scroll-mt-2">
                <InternalFormCreditReports
                  submission={submission}
                  pulledCreditReports={pulledCreditReports}
                  onReportsChanged={fetchSubmission}
                  isProviderPickerOpen={isCreditReportPickerOpen}
                  setIsProviderPickerOpen={setIsCreditReportPickerOpen}
                />
              </div>

              {submission.status === 'APPROVED' && isEclipse && (
                <SectionCard id={sectionAnchor('Eclipse Updates')} title="Eclipse Updates">
                  <div className="flex flex-col gap-2">
                    {(submission.eclipseSyncResults ?? []).length === 0 ? (
                      <p className="text-xs GREY_500-CLR">
                        This form was approved without changing anything in Eclipse.
                      </p>
                    ) : (
                      (submission.eclipseSyncResults ?? []).map((item) => (
                        <div
                          key={item.key}
                          className="flex flex-col gap-1 text-xs border-b border-gray-100 pb-2 last:border-b-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium">{item.label}</span>
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-md border font-medium ${
                                SYNC_STATUS_STYLES[item.status].className
                              }`}>
                              {SYNC_STATUS_STYLES[item.status].label}
                            </span>
                          </div>
                          {item.eclipseId && (
                            <p className="GREY_600-CLR">{`Eclipse #${item.eclipseId}`}</p>
                          )}
                          {item.message && (
                            <p className="GREY_600-CLR whitespace-pre-wrap break-words">
                              {item.message}
                            </p>
                          )}
                          <p className="GREY_400-CLR">{formatDate(item.attemptedAt)}</p>
                        </div>
                      ))
                    )}
                  </div>
                </SectionCard>
              )}
              {submission.status === 'APPROVED' && !isEclipse && (
                <SectionCard
                  id={sectionAnchor('P21 Updates')}
                  title="P21 Updates"
                  action={
                    submission.p21SyncResults.some((item) => item.status === 'FAILED') &&
                    !isSalesRole ? (
                      <DillButton
                        label="Retry Failed"
                        variant="secondary"
                        height={28}
                        onClick={handleRetryP21Sync}
                        isLoading={isRetrying}
                        isDisabled={isRetrying}
                      />
                    ) : null
                  }>
                  <div className="flex flex-col gap-2">
                    {submission.p21SyncResults.length === 0 ? (
                      <p className="text-xs GREY_500-CLR">
                        This form had no changes to push to P21.
                      </p>
                    ) : (
                      submission.p21SyncResults.map((item) => (
                        <div
                          key={item.key}
                          className="flex flex-col gap-1 text-xs border-b border-gray-100 pb-2 last:border-b-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium">{item.label}</span>
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-md border font-medium ${
                                SYNC_STATUS_STYLES[item.status].className
                              }`}>
                              {SYNC_STATUS_STYLES[item.status].label}
                            </span>
                          </div>
                          {item.p21Id && <p className="GREY_600-CLR">{`P21 #${item.p21Id}`}</p>}
                          {item.message && (
                            <p className="GREY_600-CLR whitespace-pre-wrap break-words">
                              {item.message}
                            </p>
                          )}
                          <p className="GREY_400-CLR">{formatDate(item.attemptedAt)}</p>
                        </div>
                      ))
                    )}
                  </div>
                </SectionCard>
              )}
            </div>
          </div>
        </div>

        <div className="w-full lg:w-1/4 flex flex-col gap-4 lg:h-full lg:overflow-y-auto">
          <div className="h-fit bg-white border border-gray-200 rounded-xl shadow-sm">
            <div className="flex items-center justify-between px-4 py-1 bg-gray-50 border-b border-gray-100 rounded-t-xl">
              <h3 className="text-sm font-bold text-gray-800 tracking-wide">Branch & Sales Reps</h3>
              {!isSalesRole && !isEditingBranch && (
                <DillButton
                  variant="ghost-blue"
                  label={currentBranch ? 'Edit' : 'Assign'}
                  onClick={() => {
                    setDraftBranchId(currentBranch?.branchId ?? '');
                    setDraftSalesRepId(getBranchSalesRepKey(currentSalesReps[0]));
                    setIsEditingBranch(true);
                  }}
                />
              )}
            </div>
            <div className="px-4 py-3 flex flex-col gap-2">
              {isEditingBranch ? (
                <>
                  <AppDropDown
                    label="Branch"
                    value={draftBranchId}
                    items={supplierBranches.map((branch) => ({
                      label: formatBranchLabel(branch),
                      value: branch.branchId,
                    }))}
                    onSelectChange={(item) => {
                      setDraftBranchId(item?.value ?? '');
                      setDraftSalesRepId('');
                    }}
                  />
                  <AppDropDown
                    label="Sales Rep"
                    value={draftSalesRepId}
                    items={(draftBranch?.salesRep ?? []).map((rep) => ({
                      label: rep?.name || rep?.erpId || '',
                      value: getBranchSalesRepKey(rep),
                    }))}
                    onSelectChange={(item) => setDraftSalesRepId(item?.value ?? '')}
                  />
                  <div className="flex justify-end gap-2">
                    <DillButton
                      label="Cancel"
                      variant="secondary"
                      height={30}
                      isDisabled={isSavingBranch}
                      onClick={() => setIsEditingBranch(false)}
                    />
                    <DillButton
                      label="Save"
                      height={30}
                      isLoading={isSavingBranch}
                      isDisabled={isSavingBranch}
                      onClick={handleSaveBranch}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p className="text-[11px] font-semibold text-gray-500 mb-0.5">Branch</p>
                    <p className="text-sm text-gray-900 truncate">
                      {formatBranchLabel(currentBranch) || '-'}
                    </p>
                  </div>
                  {currentSalesReps.length > 0 && (
                    <div>
                      <p className="text-[11px] font-semibold text-gray-500 mb-0.5">Sales Rep</p>
                      <p className="text-sm text-gray-900 truncate">
                        {currentSalesReps.map((rep) => rep?.name).join(', ')}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="h-fit bg-white border border-gray-200 rounded-xl shadow-sm">
            <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-100 rounded-t-xl">
              <h3 className="text-sm font-bold text-gray-800 tracking-wide">Steps</h3>
            </div>
            <div className="py-1">
              {navSteps.map((step) => {
                const isActive = activeStep === step;
                return (
                  <div
                    key={step}
                    onClick={() => {
                      setActiveStep(step);
                      document
                        .getElementById(sectionAnchor(step))
                        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className={`flex items-center gap-2.5 px-4 py-2 text-sm transition-all duration-200 cursor-pointer ${
                      isActive
                        ? 'bg-blue-50 text-blue-500 font-semibold'
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}>
                    <div
                      className={`w-1.5 h-1.5 rounded-full shrink-0 transition-all duration-200 ${
                        isActive ? 'bg-blue-500' : 'bg-gray-300'
                      }`}
                    />
                    <span className="truncate">{step}</span>
                  </div>
                );
              })}
            </div>
          </div>
          {canDecide && (
            <div className="bg-white p-4 rounded-xl border border-gray-200 flex flex-col gap-3">
              <p className="text-sm font-semibold GREY_800-CLR">Credit Team Decision</p>
              <div className="w-full grid grid-cols-2 gap-2">
                <DillButton
                  label="Reject"
                  variant="secondary"
                  width="100%"
                  height={36}
                  className="w-full flex justify-center"
                  onClick={() => {
                    setNotes('');
                    setRejectNotifyEmails(dedupe(defaultNotifyEmails));
                    setIsRejectModalOpen(true);
                  }}
                  isDisabled={!!decidingAs}
                />
                <DillButton
                  label="Approve"
                  width="100%"
                  height={36}
                  className="w-full flex justify-center"
                  onClick={() => setIsApproveModalOpen(true)}
                  isLoading={decidingAs === 'APPROVED'}
                  isDisabled={!!decidingAs}
                />
              </div>
            </div>
          )}
          {(submission.status === 'APPROVED' || submission.status === 'REJECTED') && (
            <div className="bg-white p-4 rounded-xl border border-gray-200 flex flex-col gap-1 text-sm">
              <p className="text-sm font-semibold GREY_800-CLR">Decision</p>
              <p>
                {`${submission.status === 'APPROVED' ? 'Approved' : 'Rejected'} by ${
                  submission.decidedByName || '-'
                } on ${formatDate(submission.decidedAt)}`}
              </p>
              {submission.decisionNotes && (
                <p className="GREY_600-CLR whitespace-pre-wrap">{submission.decisionNotes}</p>
              )}
              {!isSalesRole && !!submission.decidedByUserId && (
                <div className="w-full flex flex-col mt-2">
                  <DillButton
                    label="Revert Decision"
                    width="100%"
                    height={36}
                    className="w-full flex justify-center"
                    onClick={() => {
                      setRevertReason('');
                      setIsRevertModalOpen(true);
                    }}
                  />
                </div>
              )}
            </div>
          )}
          {submission.status === 'APPROVED' && approvalSummaryRows.length > 0 && (
            <div className="bg-white p-4 rounded-xl border border-gray-200 flex flex-col gap-2 text-sm">
              {approvalSummaryRows.map((row) => (
                <div key={row.label} className="flex items-center justify-between gap-3">
                  <span className="GREY_700-CLR">{row.label}</span>
                  <span className="font-semibold text-gray-900 text-right">{row.value}</span>
                </div>
              ))}
            </div>
          )}
          <div className="rounded-xl border border-gray-100 bg-white p-4">
            <CreditAppNotes
              notes={submission.internalNotes ?? []}
              creditAppId={submission.id}
              isSalesRole={isSalesRole}
              onSaveNotes={(internalNotes) => saveDetails({ internalNotes })}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternalFormSubmissionDetails;
